import axios from 'axios';

let cancelTokenSources = [];

const AUTH_TOKEN_KEY = 'mantraz_auth_token';
const REFRESH_TOKEN_KEY = 'mantraz_refresh_token';
const REFRESH_PATH = '/security/authentication/refresh';
const AUTH_ERROR_CODE = 411;
// Flag-flag UI session yang harus ikut dibersihkan saat logout / auth gagal.
const SESSION_STATE_KEYS = ['isAuthenticated', 'userId', 'activeCompany', 'activeGroup', 'activeApp'];

const baseURL = process.env.VUE_APP_API_URL ? process.env.VUE_APP_API_URL : '/';

const axiosInstance = axios.create({
  baseURL,
});

export function getNewCancelToken() {
  const source = axios.CancelToken.source();
  cancelTokenSources.push(source);
  return source.token;
}

export function cancelPendingRequests() {
  cancelTokenSources.forEach(source => source.cancel('Request Cencelled.'));
  cancelTokenSources = [];
}

// --- Token storage ---------------------------------------------------------

// Simpan access token & refresh token. Dipakai saat login dan saat rotasi token.
export function setAuthTokens(token, refreshToken) {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

// Bersihkan seluruh state auth: tokens + flag session.
// sessionStorage juga ikut dibersihkan utk membuang sisa state dari versi lama.
export function clearAuthSession() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  SESSION_STATE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

// --- Refresh token handling ------------------------------------------------

// True ketika API membalas auth error (HTTP 404 dengan errorCode 411).
function isAuthError(error) {
  return !!(error && error.response
    && error.response.data
    && error.response.data.status
    && error.response.data.status.errorCode === AUTH_ERROR_CODE);
}

// Bersihkan sesi lalu arahkan ke halaman login.
function handleAuthFailure() {
  clearAuthSession();
  if (!window.location.pathname.includes('/auth/login')) {
    window.location.href = '/auth/login';
  }
}

let isRefreshing = false;
let refreshPromise = null;

// Tukar refreshToken dengan token baru. Memakai axios polos (tanpa interceptor)
// supaya kegagalan refresh tidak memicu refresh berulang.
function refreshAuthToken() {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }

  const formData = new FormData();
  formData.append('refreshToken', refreshToken);

  return axios
    .post(`${baseURL}${REFRESH_PATH}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => {
      const data = res.data || {};
      if (!data.token) {
        throw new Error('Refresh response missing token');
      }
      // Token & refreshToken keduanya berotasi pada setiap refresh.
      setAuthTokens(data.token, data.refreshToken);
      return data.token;
    });
}

// --- Interceptors ----------------------------------------------------------

function applyAuthInterceptors(instance) {
  // Selalu sisipkan token terbaru dari localStorage pada setiap request.
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authentication = token;
    }
    return config;
  });

  // Tangkap auth error: refresh token sekali, lalu ulangi request aslinya.
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const originalRequest = error.config;

      if (!originalRequest || !isAuthError(error)) {
        return Promise.reject(error);
      }

      // Sudah pernah di-retry -> refresh tidak menolong, hentikan sesi.
      if (originalRequest._retry) {
        handleAuthFailure();
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      // Refresh hanya dijalankan sekali walau banyak request gagal bersamaan.
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAuthToken().then(
          (token) => {
            isRefreshing = false;
            return token;
          },
          (refreshError) => {
            isRefreshing = false;
            handleAuthFailure();
            return Promise.reject(refreshError);
          },
        );
      }

      return refreshPromise
        .then(() => instance(originalRequest))
        .catch(() => Promise.reject(error));
    },
  );

  return instance;
}

applyAuthInterceptors(axiosInstance);

// Compatibility shim for legacy services that call axiosInstance.create(...).
axiosInstance.create = function create(config = {}) {
  return applyAuthInterceptors(axios.create({
    ...axiosInstance.defaults,
    ...config,
  }));
};

export default axiosInstance;
